import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWind,
  faPlay,
  faPause,
  faRotateRight,
  faCheck,
  faExclamationTriangle,
  faBell,
  faSpinner,
  faClock,
  faFaceSmile,
  faCalendarDay,
  faStopwatch,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import mentalWellnessService, { BreathingSession } from '../../../services/mentalWellnessService';
import BackButton from '../../../components/BackButton/BackButton';
import './BreathingExercise.css';
import { useProfile } from '../../../context/ProfileContext';

/* ================================================================== */
/*  Types                                                              */
/* ================================================================== */

interface ToastState {
  message: string;
  type: 'success' | 'error' | 'info';
}

type Phase = 'inhale' | 'hold1' | 'exhale' | 'hold2' | 'idle';

interface Technique {
  id: string;
  name: string;
  description: string;
  color: string;
  phases: { phase: Phase; duration: number; label: string }[];
}

/* ================================================================== */
/*  Technique Definitions                                              */
/* ================================================================== */

const TECHNIQUES: Technique[] = [
  {
    id: 'box',
    name: 'Box Breathing',
    description: '4-4-4-4 pattern. Used by Navy SEALs to stay calm under pressure.',
    color: '#7c3aed',
    phases: [
      { phase: 'inhale', duration: 4, label: 'Breathe In' },
      { phase: 'hold1', duration: 4, label: 'Hold' },
      { phase: 'exhale', duration: 4, label: 'Breathe Out' },
      { phase: 'hold2', duration: 4, label: 'Hold' },
    ],
  },
  {
    id: 'deep',
    name: 'Deep Breathing',
    description: 'Simple 4-4 pattern. Perfect for beginners and quick calm.',
    color: '#06b6d4',
    phases: [
      { phase: 'inhale', duration: 4, label: 'Breathe In' },
      { phase: 'exhale', duration: 4, label: 'Breathe Out' },
    ],
  },
  {
    id: '478',
    name: '4-7-8 Technique',
    description: 'Developed by Dr. Weil. Excellent for sleep and deep relaxation.',
    color: '#10b981',
    phases: [
      { phase: 'inhale', duration: 4, label: 'Breathe In' },
      { phase: 'hold1', duration: 7, label: 'Hold' },
      { phase: 'exhale', duration: 8, label: 'Breathe Out' },
    ],
  },
];

/* ================================================================== */
/*  Helpers                                                            */
/* ================================================================== */

const formatDate = (iso: string): string => {
  if (!iso) return '--';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatDuration = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

/* ================================================================== */
/*  Toast Component                                                    */
/* ================================================================== */

const Toast: React.FC<{ message: string; type: string; onClose: () => void }> = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const iconMap: Record<string, IconDefinition> = { success: faCheck, error: faExclamationTriangle, info: faBell };

  return (
    <div className={`mw-br-toast mw-br-toast-${type}`}>
      <span className="mw-br-toast-icon"><FontAwesomeIcon icon={iconMap[type] || faBell} /></span>
      <span className="mw-br-toast-message">{message}</span>
      <button className="mw-br-toast-close" onClick={onClose}>{'\u00D7'}</button>
    </div>
  );
};

/* ================================================================== */
/*  Main Component                                                     */
/* ================================================================== */

const BreathingExercise: React.FC = () => {
  const { t } = useTranslation(['common']);
  const { activeProfile } = useProfile();

  // Data
  const [sessions, setSessions] = useState<BreathingSession[]>([]);
  const [loading, setLoading] = useState(true);

  // Exercise state
  const [selectedTechnique, setSelectedTechnique] = useState<Technique>(TECHNIQUES[0]);
  const [isRunning, setIsRunning] = useState(false);
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState(0);
  const [phaseTimer, setPhaseTimer] = useState(0);
  const [totalElapsed, setTotalElapsed] = useState(0);
  const [cycleCount, setCycleCount] = useState(0);
  const [sessionComplete, setSessionComplete] = useState(false);

  // Mood before/after
  const [moodBefore, setMoodBefore] = useState(0);
  const [moodAfter, setMoodAfter] = useState(0);
  const [showMoodBefore, setShowMoodBefore] = useState(false);
  const [showMoodAfter, setShowMoodAfter] = useState(false);

  // UI
  const [toast, setToast] = useState<ToastState | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const showToast = useCallback((message: string, type: ToastState['type']) => {
    setToast({ message, type });
  }, []);

  // Fetch sessions
  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);
      const data = await mentalWellnessService.getBreathingSessions();
      setSessions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch breathing sessions:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions, activeProfile]);

  // Timer logic
  useEffect(() => {
    if (!isRunning) return;

    intervalRef.current = setInterval(() => {
      setPhaseTimer((prev) => {
        const currentPhase = selectedTechnique.phases[currentPhaseIndex];
        if (prev >= currentPhase.duration) {
          // Move to next phase
          const nextIndex = currentPhaseIndex + 1;
          if (nextIndex >= selectedTechnique.phases.length) {
            // Complete a cycle
            setCycleCount((c) => c + 1);
            setCurrentPhaseIndex(0);
          } else {
            setCurrentPhaseIndex(nextIndex);
          }
          return 0;
        }
        return prev + 1;
      });
      setTotalElapsed((prev) => prev + 1);
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isRunning, currentPhaseIndex, selectedTechnique]);

  // Derive current phase info
  const currentPhase = selectedTechnique.phases[currentPhaseIndex];
  const phaseProgress = currentPhase ? phaseTimer / currentPhase.duration : 0;

  // Circle scale: grows on inhale, shrinks on exhale, steady on hold
  const getCircleScale = (): number => {
    if (!isRunning && totalElapsed === 0) return 0.6;
    const phase = currentPhase?.phase || 'idle';
    switch (phase) {
      case 'inhale': return 0.6 + 0.4 * phaseProgress;
      case 'exhale': return 1.0 - 0.4 * phaseProgress;
      case 'hold1': return 1.0;
      case 'hold2': return 0.6;
      default: return 0.6;
    }
  };

  // Controls
  const handleStart = () => {
    if (totalElapsed === 0) {
      setShowMoodBefore(true);
      return;
    }
    setIsRunning(true);
  };

  const handleStartAfterMood = () => {
    setShowMoodBefore(false);
    setIsRunning(true);
  };

  const handlePause = () => {
    setIsRunning(false);
  };

  const handleReset = () => {
    setIsRunning(false);
    setCurrentPhaseIndex(0);
    setPhaseTimer(0);
    setTotalElapsed(0);
    setCycleCount(0);
    setSessionComplete(false);
    setMoodBefore(0);
    setMoodAfter(0);
  };

  const handleFinish = () => {
    setIsRunning(false);
    setShowMoodAfter(true);
  };

  const handleSaveSession = async () => {
    setShowMoodAfter(false);
    setSessionComplete(true);

    try {
      const payload: any = {
        technique: selectedTechnique.id,
        date: new Date().toISOString().split('T')[0],
        duration_seconds: totalElapsed,
        cycles_completed: cycleCount,
      };
      if (moodBefore > 0) payload.mood_before = moodBefore;
      if (moodAfter > 0) payload.mood_after = moodAfter;

      await mentalWellnessService.createBreathingSession(payload);
      showToast('Breathing session saved!', 'success');
      await fetchSessions();
    } catch (err) {
      console.error('Failed to save breathing session:', err);
      showToast('Failed to save session.', 'error');
    }
  };

  const handleTechniqueSelect = (tech: Technique) => {
    if (isRunning) return;
    setSelectedTechnique(tech);
    handleReset();
  };

  const MOOD_EMOJIS = [
    { value: 1, emoji: '\uD83D\uDE22', label: 'Bad' },
    { value: 2, emoji: '\uD83D\uDE15', label: 'Low' },
    { value: 3, emoji: '\uD83D\uDE10', label: 'Okay' },
    { value: 4, emoji: '\uD83D\uDE0A', label: 'Good' },
    { value: 5, emoji: '\uD83E\uDD29', label: 'Great' },
  ];

  /* ================================================================ */
  /*  Render: Mood Prompt                                              */
  /* ================================================================ */

  const renderMoodPrompt = (
    title: string,
    value: number,
    setValue: (v: number) => void,
    onSubmit: () => void,
  ) => (
    <div className="mw-br-modal-overlay" onClick={() => {}}>
      <div className="mw-br-mood-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{title}</h3>
        <div className="mw-br-mood-options">
          {MOOD_EMOJIS.map((m) => (
            <button
              key={m.value}
              className={`mw-br-mood-opt ${value === m.value ? 'active' : ''}`}
              style={value === m.value ? { borderColor: selectedTechnique.color, background: `${selectedTechnique.color}12` } : {}}
              onClick={() => setValue(m.value)}
            >
              <span className="mw-br-mood-emoji">{m.emoji}</span>
              <span className="mw-br-mood-label">{m.label}</span>
            </button>
          ))}
        </div>
        <button
          className="mw-br-btn mw-br-btn-primary"
          style={{ background: `linear-gradient(135deg, ${selectedTechnique.color}, ${selectedTechnique.color}cc)` }}
          onClick={onSubmit}
          disabled={value === 0}
        >
          Continue
        </button>
      </div>
    </div>
  );

  /* ================================================================ */
  /*  Main Render                                                      */
  /* ================================================================ */

  return (
    <div className="mw-br-page">
      {toast && (
        <div className="mw-br-toast-container">
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        </div>
      )}

      {showMoodBefore && renderMoodPrompt('How are you feeling right now?', moodBefore, setMoodBefore, handleStartAfterMood)}
      {showMoodAfter && renderMoodPrompt('How are you feeling after the session?', moodAfter, setMoodAfter, handleSaveSession)}

      {/* Hero */}
      <section className="mw-br-hero">
        <div className="mw-br-hero-circle mw-br-hero-circle-1" />
        <div className="mw-br-hero-circle mw-br-hero-circle-2" />
        <Container>
          <BackButton />
          <div className="mw-br-hero-content">
            <h1 className="mw-br-hero-title">Guided Breathing</h1>
            <p className="mw-br-hero-subtitle">
              Calm your mind and reduce stress with guided breathing exercises. Choose a technique and follow along.
            </p>
          </div>
        </Container>
      </section>

      <Container>
        {/* Technique Selector */}
        <div className="mw-br-technique-grid">
          {TECHNIQUES.map((tech) => (
            <button
              key={tech.id}
              className={`mw-br-technique-card ${selectedTechnique.id === tech.id ? 'active' : ''}`}
              style={selectedTechnique.id === tech.id ? { borderColor: tech.color, borderTopColor: tech.color } : {}}
              onClick={() => handleTechniqueSelect(tech)}
              disabled={isRunning}
            >
              <div className="mw-br-technique-dot" style={{ background: tech.color }} />
              <h4>{tech.name}</h4>
              <p>{tech.description}</p>
            </button>
          ))}
        </div>

        {/* Breathing Circle */}
        <div className="mw-br-exercise-area">
          <div className="mw-br-circle-container">
            <div
              className="mw-br-circle-outer"
              style={{
                borderColor: `${selectedTechnique.color}25`,
              }}
            >
              <div
                className="mw-br-circle-inner"
                style={{
                  transform: `scale(${getCircleScale()})`,
                  background: `radial-gradient(circle, ${selectedTechnique.color}30, ${selectedTechnique.color}08)`,
                  borderColor: selectedTechnique.color,
                  transition: `transform ${isRunning ? '1s' : '0.3s'} ease-in-out`,
                }}
              >
                <div className="mw-br-circle-text">
                  {sessionComplete ? (
                    <>
                      <span className="mw-br-phase-label" style={{ color: selectedTechnique.color }}>Session Complete!</span>
                      <span className="mw-br-phase-timer">{cycleCount} cycles</span>
                    </>
                  ) : isRunning || totalElapsed > 0 ? (
                    <>
                      <span className="mw-br-phase-label" style={{ color: selectedTechnique.color }}>
                        {currentPhase?.label || 'Ready'}
                      </span>
                      <span className="mw-br-phase-timer">
                        {currentPhase ? currentPhase.duration - phaseTimer : 0}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="mw-br-phase-label" style={{ color: selectedTechnique.color }}>Ready</span>
                      <span className="mw-br-phase-sublabel">Press Start</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Info row */}
          <div className="mw-br-info-row">
            <div className="mw-br-info-item">
              <FontAwesomeIcon icon={faStopwatch} />
              <span>{formatDuration(totalElapsed)}</span>
            </div>
            <div className="mw-br-info-item">
              <FontAwesomeIcon icon={faRotateRight} />
              <span>{cycleCount} cycles</span>
            </div>
          </div>

          {/* Controls */}
          <div className="mw-br-controls">
            {!isRunning && !sessionComplete && (
              <button
                className="mw-br-ctrl-btn mw-br-ctrl-start"
                style={{ background: `linear-gradient(135deg, ${selectedTechnique.color}, ${selectedTechnique.color}cc)` }}
                onClick={handleStart}
              >
                <FontAwesomeIcon icon={faPlay} /> Start
              </button>
            )}
            {isRunning && (
              <>
                <button className="mw-br-ctrl-btn mw-br-ctrl-pause" onClick={handlePause}>
                  <FontAwesomeIcon icon={faPause} /> Pause
                </button>
                <button
                  className="mw-br-ctrl-btn mw-br-ctrl-finish"
                  style={{ background: `linear-gradient(135deg, ${selectedTechnique.color}, ${selectedTechnique.color}cc)` }}
                  onClick={handleFinish}
                >
                  <FontAwesomeIcon icon={faCheck} /> Finish
                </button>
              </>
            )}
            {(totalElapsed > 0 || sessionComplete) && !isRunning && (
              <button className="mw-br-ctrl-btn mw-br-ctrl-reset" onClick={handleReset}>
                <FontAwesomeIcon icon={faRotateRight} /> Reset
              </button>
            )}
          </div>
        </div>

        {/* Session History */}
        <div className="mw-br-history-card">
          <div className="mw-br-history-header">
            <h3>
              <FontAwesomeIcon icon={faClock} />
              Recent Sessions
            </h3>
            <span className="mw-br-history-count">{sessions.length} sessions</span>
          </div>
          {loading ? (
            <div className="mw-br-history-loading">
              <FontAwesomeIcon icon={faSpinner} spin /> Loading sessions...
            </div>
          ) : sessions.length === 0 ? (
            <div className="mw-br-history-empty">
              <FontAwesomeIcon icon={faWind} />
              <p>No sessions yet. Complete your first breathing exercise above.</p>
            </div>
          ) : (
            <div className="mw-br-history-list">
              {sessions.slice(0, 10).map((session) => (
                <div className="mw-br-history-item" key={session.id}>
                  <div className="mw-br-history-left">
                    <span className="mw-br-history-technique">{session.technique_display || session.technique}</span>
                    <span className="mw-br-history-date">{formatDate(session.date)}</span>
                  </div>
                  <div className="mw-br-history-right">
                    <span className="mw-br-history-stat">{formatDuration(session.duration_seconds)}</span>
                    <span className="mw-br-history-stat">{session.cycles_completed} cycles</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Container>
    </div>
  );
};

export default BreathingExercise;
